"use client";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown, SearchIcon } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Route } from "next";
import style from "./styles.module.css";

export type Value = {
  label: string;
  value: string;
  url?: Route;
};

export type ValueGroup = {
  group_label: string;
  group_value: string;
  values: Value[];
};

type ComboBoxProps = {
  values: Value[];
  entity: string;
  placeholder?: string;
  keyboardShortcut?: string;
  icon?: ReactNode;
  className?: string;
  value?: string;
  name?: string;
  onChange?: (value: string) => void;
};

export function Combobox({
  values = [],
  entity,
  placeholder,
  keyboardShortcut,
  className,
  value,
  name,
  icon = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />,
}: ComboBoxProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>();
  const [vals, setVals] = useState(values);
  const [search, setSearch] = useState("");

  // const router = useRouter();

  // const goToPage = (url: Route | undefined) => {
  //   if (!url) return;
  //   router.push(url);
  // };

  useEffect(() => {
    if (search) {
      setVals(
        values.filter((val2) =>
          val2.label.toLowerCase().includes(search.toLowerCase()),
        ),
      );
    } else {
      setVals(values);
    }
  }, [search, values]);

  useEffect(() => {}, []);

  useEffect(() => {
    if (value) {
      setSelected(
        (values as Value[]).find((val) => val.value === value)?.value,
      );
    }
  }, [value, values]);

  useEffect(() => {
    return () => {
      setTimeout(() => {
        setSearch("");
      }, 200);
    };
  }, [open]);

  useEffect(() => {
    if (!keyboardShortcut) return;
    const down = (e: KeyboardEvent) => {
      if (e.key === keyboardShortcut && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [keyboardShortcut]);

  const getLabel = (val: string) => {
    return (values as Value[]).find((val2) => val2.value === val)?.label;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between", className)}
        >
          {placeholder
            ? placeholder
            : selected
              ? getLabel(selected)
              : `Select ${entity}`}
          {icon}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-full p-0", className, style.PopoverContent)}
      >
        <input type="hidden" name={name} />
        <div className="flex items-center justify-between border-b-[1px]">
          <SearchIcon className="ml-4 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            className="bg-inherit p-4 shadow-none ring-0 border-0 w-full outline-none"
            placeholder={`Search ${entity}s...`}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </div>

        <ScrollArea className="max-h-[200px] w-full rounded-md p-2">
          <div className="flex flex-col gap-2">
            {vals.length === 0 && (
              <div className="flex flex-col gap-2 p-4">
                <div className="text-sm text-muted-foreground">
                  No {entity}s found.
                </div>
              </div>
            )}
            {vals.map((val) => (
              <Button
                key={val.value}
                className="flex gap-2 w-full text-left justify-start"
                variant="ghost"
                onClick={() => setSelected(val.value)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selected === (val as Value).value
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                {(val as Value).label}
              </Button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
